import React, { useContext, useMemo, useRef, useState } from 'react';
import { GridColumn, Grid } from '@progress/kendo-react-grid';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import GridWrapper from '~/src/components/common/GridWrapper';
import { withState } from '../../../../components/common/grid/WrappedGrid';
import { API_URL } from '../../../../utils/constants';
import GtinComponent from '~/src/components/common/GtinComponent';
import { Link } from 'react-router-dom';
import { GOMBA_PRODUCT_SYNCHRONIZATION_STATUS } from '~/src/utils/gombaStatuses';
import DisplayDate from '~/src/components/common/DisplayDate';
import MyContext from '~/src/services/context';
import GombaProductsApi from '~/src/services/api/GombaProductsApi';
import styled from 'styled-components';
import TableButton from '~/src/components/common/forms/Button/TableButton';
import { INTEGRATION_TYPE } from '~/src/utils/integrationsConstants';
import ApiResultModal from '~/src/scenes/products/genericProducts/components/ApiResultModal';

const GENERICS_URL = `${API_URL}/products/generics/get`;
const GOMBA_PRODUCTS_URL = `${API_URL}/gomba/products/get-all`;

const gtinsCell = ({ dataItem }) => {
  const gtinsArr = dataItem?.gtins;
  if (!Array.isArray(gtinsArr) || gtinsArr.length === 0) return <td />;
  return (
    <td>
      {gtinsArr.map((gtin, idx) => (
        <div key={idx} style={{ marginBottom: 4 }}>
          {gtin.cuGtin && (
            <GtinComponent text="CU" code={gtin.cuGtin} background="#F4F4F4" />
          )}
        </div>
      ))}
    </td>
  );
};

const genericProductNameCell = (row) => {
  const { dataItem } = row;
  return (
    <td>
      <Link to={`/products/edit/${dataItem.id}`}>{dataItem.name}</Link>{' '}
    </td>
  );
};

const gombaGtinCell = (row) => {
  const { dataItem } = row;
  return (
    <td>{dataItem?.code && (
      <GtinComponent text="CU" code={dataItem.code} background="#F4F4F4" />
    )}
    </td>
  );
};

const gombaProductNameCell = (props) => <td>{props.dataItem?.name}</td>;

export default function GombaProductsList(): JSX.Element {
  const GenericsGrid = useMemo(() => withState(Grid, GENERICS_URL, { stateKey: 'genericsGrid' }), []);
  const GombaGrid = useMemo(() => withState(Grid, GOMBA_PRODUCTS_URL, { stateKey: 'gombaGrid' }), []);
  const [modalMessage, setModalMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [gridKey, setGridKey] = useState(0);
  const context = useContext(MyContext);

  const synchronizationStatusCell = (row) => {
    const { dataItem } = row;
    return (
      <td>
        {
          Object.values(GOMBA_PRODUCT_SYNCHRONIZATION_STATUS).find(
            (s) => s.value === dataItem?.gombaProductInfo?.synchronizationStatus
          )?.label ||
          GOMBA_PRODUCT_SYNCHRONIZATION_STATUS.NOT_SYNCRONIZED.label
        }

        {dataItem?.gombaProductInfo?.lastSynchronization && (
          <>
            <br />
            Last Synchronization:
            <DisplayDate date={dataItem.gombaProductInfo.lastSynchronization} />
          </>
        )}
      </td>
    );
  };

  const gombaActionsCell = ({ dataItem }) => {
    const gombaProductId = dataItem?.id;

    return (
      <td style={{ paddingTop: 2, paddingBottom: 2 }}>
        <ActionsCellWrapper>
          {gombaProductId && (
            <TableButton
              onClick={async () => {
                try {
                  let result = await GombaProductsApi.removeGombaProductByGombaId(gombaProductId)
                  const success = result?.data?.success;
                  setModalMessage(result?.data?.message || (success ? 'Operation completed' : 'Operation failed'));

                  if (success) {
                    setGridKey(prev => prev + 1);
                  }

                  setModalOpen(true);

                } catch (e) {
                  setModalMessage('Operation failed: ' + (e?.message || 'Unknown error'));
                  setModalOpen(true);
                }
              }}
              type="button"
              title="Remove"
            />
          )}
        </ActionsCellWrapper>
      </td>
    );
  }

  const actionsCell = ({ dataItem }) => {
    const syncStatus = dataItem?.gombaProductInfo?.synchronizationStatus;
    const gombaProductInfoId = dataItem?.gombaProductInfo?.id;
    const genericProductId = dataItem?.id;

    const handleApiAction = async (action) => {
      try {
        let result;
        if (action === 'create') {
          result = await GombaProductsApi.createGombaProduct(genericProductId);
        } else if (action === 'update') {
          result = await GombaProductsApi.updateGombaProduct(gombaProductInfoId);
        } else if (action === 'remove') {
          result = await GombaProductsApi.removeGombaProduct(gombaProductInfoId);
        }

        const success = result?.data?.success;
        setModalMessage(result?.data?.message || (success ? 'Operation completed' : 'Operation failed'));

        if (success) {
          setGridKey(prev => prev + 1);

          if (result?.data?.gombaProductPageUrl) {
            window.open(result.data.gombaProductPageUrl, '_blank');
          }
        }

        setModalOpen(true);

      } catch (e) {
        setModalMessage('Operation failed: ' + (e?.message || 'Unknown error'));
        setModalOpen(true);
      }
    };

    const showCreate =
      !dataItem.gombaProductInfo ||
      syncStatus === GOMBA_PRODUCT_SYNCHRONIZATION_STATUS.NOT_SYNCRONIZED.value ||
      syncStatus === GOMBA_PRODUCT_SYNCHRONIZATION_STATUS.REMOVED.value;

    const showUpdate =
      !!dataItem.gombaProductInfo &&
      syncStatus === GOMBA_PRODUCT_SYNCHRONIZATION_STATUS.SYNCHRONIZED.value;

    const showRemove = showUpdate;

    return (
      <td style={{ paddingTop: 2, paddingBottom: 2 }}>
        <ActionsCellWrapper>
          {showCreate && (
            <TableButton
              onClick={() => handleApiAction('create')}
              type="button"
              title="Create"
            />
          )}
          {showUpdate && (
            <TableButton
              onClick={() => handleApiAction('update')}
              type="button"
              title="Update"
            />
          )}
          {showRemove && (
            <TableButton
              onClick={() => handleApiAction('remove')}
              type="button"
              title="Remove"
            />
          )}
        </ActionsCellWrapper>
      </td>
    );
  };

  const gombaColumns = context.user?.activeIntegrations?.includes(INTEGRATION_TYPE.GOMBA_SHOP.label)
    ? [
      <GridColumn
        key="synchronizationStatus"
        field="synchronizationStatus"
        title="Synchronization Status"
        cell={synchronizationStatusCell}
        filterable={false}
        sortable={false}
      />,
      <GridColumn
        key="actions"
        field="actions"
        title="Gomba Actions"
        filterable={false}
        sortable={false}
        cell={actionsCell}
      />,
    ]
    : [];

  return (
    <WhitePad isFluid>
      <div style={{ display: 'flex', gap: 32 }}>
        <GridWrapper>
          <div style={{ flex: 1 }}>
            <h4>Generics Products</h4>
            <ApiResultModal
              open={modalOpen}
              message={modalMessage}
              onClose={() => {
                setModalOpen(false);
              }}
            />
            <GenericsGrid gridId="genericsGrid" key={gridKey}>
              <GridColumn field="name" title="Name" cell={genericProductNameCell} />
              <GridColumn field="gtin" title="GTINs" cell={gtinsCell} />
              {gombaColumns}
            </GenericsGrid>
          </div>
        </GridWrapper>
        <GridWrapper>
          <div style={{ flex: 1 }}>
            <h4>Gomba Products</h4>
            <GombaGrid gridId="gombaGrid" key={gridKey}>
              <GridColumn field="name" title="Name" cell={gombaProductNameCell} />
              <GridColumn field="code" title="GTINs" cell={gombaGtinCell} />
              <GridColumn
                field="gombaActions"
                title="Gomba Actions"
                filterable={false}
                sortable={false}
                cell={gombaActionsCell}
              />
            </GombaGrid>
          </div>
        </GridWrapper>
      </div>
    </WhitePad>
  );
}

const ActionsCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  justify-content: center;
  padding: 0;
  margin: 0;
`;